"use client";

import type { ReactNode } from "react";

/**
 * Class string used for the borderless title input shared between the manual
 * task creation modal and the sequence step CREATE_TASK config. Kept as a
 * single exported constant so both callers stay in sync visually.
 */
export const borderlessInputClasses =
  "border-0 bg-transparent text-h3 shadow-none !ring-0 !outline-none p-0 rounded-none";

/**
 * Class string for the borderless description editor/textarea used underneath
 * the title. Mirrors the look of the manual task modal's description field.
 *
 * `!overflow-visible` matters: the editor's own base classes set
 * `overflow-hidden`, which clips anything sitting past the text box — mention
 * chips and the reference suggestion list among them. A field with no border
 * or background has nothing to clip to, so the corners go square too.
 */
export const borderlessDescriptionClasses =
  "!border-0 !bg-transparent !shadow-none !ring-0 !p-0 !rounded-none !overflow-visible min-h-[80px] resize-none";

interface TaskFormBodyProps {
  /**
   * Optional borderless title input (e.g. <Input> or <TiptapEditor>). Omit
   * when the caller renders the title elsewhere (e.g. the manual modal keeps
   * it inside <DialogHeader>).
   */
  title?: ReactNode;
  /** Borderless description input (e.g. <TaskMentionEditor> or <Textarea>). */
  description: ReactNode;
  /** The row of *Badge components (Status, Priority, Assignee, Team, etc.). */
  badges: ReactNode;
}

/**
 * Shared container for task forms. Callers plug in their own title (optional),
 * description, and badge row. The badge row layout is standardized here so the
 * manual task modal and the sequence step CREATE_TASK config stay in sync.
 */
export function TaskFormBody({
  title,
  description,
  badges,
}: TaskFormBodyProps) {
  return (
    <div className="space-y-3">
      <div>
        {title}
        {description}
      </div>
      <div className="flex flex-wrap items-center gap-1 justify-start">
        {badges}
      </div>
    </div>
  );
}
